import Image from 'components/Common/Image/Image'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import { LUX_PLUS_MONTH_POOL_IMAGE_URL } from '../constants/luxPlusMonthTakeoverConstants'

/**
 * LuxKit List Item does not expose surface/background or hover tokens for highlight rows;
 * keep a narrow override so the default lux-plus treatment doesn't get replaced on hover,
 * matching the pattern used by NavBarHamburgerMenuMaldivesBundlesBanner.
 */
const LuxPlusMonthEventListItem = styled(ClickableListItem)`
  padding: ${rem(8)};
  min-height: ${rem(50)};
  background-color: ${props => props.theme.palette.product.luxPlus.background};
  border-radius: ${props => props.theme.borderRadius.L};

  &:not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.product.luxPlus.background};
      }
    }
  }
`

const LuxPlusMonthEventImageContainer = styled.div`
  width: ${rem(48)};
  height: ${rem(48)};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
`

// Crops in on the pool feature at the centre of the source photo — at the container's natural
// size the shot reads mostly as surrounding garden, since the pool itself only fills a small
// portion of the full image.
const ZoomedImage = styled(Image)`
  transform: scale(2.8) translateX(-2.3%) translateY(2%);
`

interface Props {
  navItem: App.NavItem
  onNavigate: () => void
}

function NavBarHamburgerMenuLuxPlusMonthEventBanner(props: Props) {
  const { navItem, onNavigate } = props
  const intl = useIntl()

  const newBadgeText = intl.formatMessage({
    id: 'UJeO-rdvZOfzyeTCI0uv',
    defaultMessage: 'New',
    description: 'New badge label for the navigation list items in the navigation bar',
  })

  return (
    <LuxPlusMonthEventListItem
      size="medium"
      startIcon={<LuxPlusMonthEventImageContainer>
        <ZoomedImage
          image={{ url: LUX_PLUS_MONTH_POOL_IMAGE_URL }}
          alt=""
          dpr={2}
          fit="center"
          objectFit="cover"
        />
      </LuxPlusMonthEventImageContainer>}
      title={<Group direction="vertical" gap={2} horizontalAlign="start">
        {navItem.newLabel && <Caption variant="small" weight="bold" format="uppercase" colour="lux-plus-contrast">
          {newBadgeText}
        </Caption>}
        <BodyText variant="medium" colour="lux-plus-contrast">
          <FormattedMessage
            id="zOYkSiqYILW2VMPltCiR"
            defaultMessage="{luxPlusLogo} Member Event"
            description="Nav item label combining the LuxPlus+ logo with 'Member Event' for the campaign navigation entry"
            values={{ luxPlusLogo: <LuxPlusLogo colour="inherit" height={12} inline /> }}
          />
        </BodyText>
      </Group>}
      href={navItem.href}
      to={navItem.to}
      onClick={onNavigate}
      rel={navItem.target === '_blank' ? 'noopener noreferrer' : undefined}
      target={navItem.target}
      endIcon={<LineAngleRightBIcon colour="lux-plus-contrast" />}
    />
  )
}

export default NavBarHamburgerMenuLuxPlusMonthEventBanner
